import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, Pill, QuillText, Select, Spinner } from '@treams/treams-ui';

import { StyrBadge } from '../../../connectors/products/StyrBadge';
import PopoverTarget from '../../../packages/kakapo-ui-kit/components/popovers/PopoverTarget';
import SettingsItem from '../../generic/SettingsItem';

export default function JobCompetency(props: {
	jobTitle: graphql.JobTitle;
	jobCompetency: graphql.JobCompetency;
	jobLevelID?: string;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const levelOptions = props.jobCompetency.competency.levels.map((level, idx) => ({
		title: localize(level.level.title).text,
		value: idx.toString(),
	}));

	const currentLevel = props.jobCompetency.competency.levels[props.jobCompetency.competencyLevel];
	const labelTitle = props.jobCompetency.label
		? localize(props.jobCompetency.label.title).text
		: undefined;

	const [attach, { loading: updating }] = graphql.useAttachJobCompetencyMutation();
	const [detach, { loading }] = graphql.useDetachJobCompetencyMutation();

	return (
		<SettingsItem
			loading={loading}
			item={{ ...props.jobCompetency, isArchived: false }}
			title={(competency) => localize(competency.competency.title).text}
			link={(competency) => `/settings/competencies/${competency.competency.id}`}
			onArchive={async () => {
				await detach({
					variables: {
						input: {
							jobTitleID: props.jobTitle.id,
							competencyID: props.jobCompetency.competency.id,
							jobLevelID: props.jobCompetency.level?.id,
						},
					},
				});
			}}
			onRefresh={() => {}}
			accessory={
				<Flex alignItems="center">
					{updating && <Spinner mr={2} />}
					<PopoverTarget
						content={
							<Box style={{ padding: 20 }}>
								<Paragraph>
									<QuillText
										value={localize(currentLevel.criteria ?? [])?.text}
										placeholder={t('evaluation.submission.competency.levels.noDescription')}
									/>
								</Paragraph>
							</Box>
						}
					>
						<Select
							small
							fit
							search={false}
							disabled={updating}
							options={levelOptions}
							selectedValue={props.jobCompetency.competencyLevel.toString()}
							onChange={async (newValue) => {
								if (newValue) {
									await attach({
										variables: {
											input: {
												jobTitleID: props.jobTitle.id,
												jobLevelID: props.jobLevelID,
												competencyID: props.jobCompetency.competency.id!,
												competencyLevel: parseInt(newValue),
											},
										},
									});
								}
							}}
						/>
					</PopoverTarget>
				</Flex>
			}
			titleBadge={
				labelTitle || props.jobCompetency.competency.styrSkillID ? (
					<>
						{labelTitle && <Pill small>{labelTitle}</Pill>}
						{props.jobCompetency.competency.styrSkillID ? <StyrBadge /> : undefined}
					</>
				) : undefined
			}
		>
			{props.jobCompetency.description && props.jobCompetency.description.length > 0 && (
				<Box mt={2}>
					<QuillText value={localize(props.jobCompetency.description).text} />
				</Box>
			)}
		</SettingsItem>
	);
}
